import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, BadgeCheck, Home } from "lucide-react";
import api from "@/lib/api";
import NavTop from "@/components/nav/nav_top";

// "Hosts": a directory of listers and realtors renters can browse as cards,
// each showing the host headshot, name, verified/realtor badge and a summary
// of their active listings (count + thumbnails). Backed by
// GET /api/listers/directory, which already excludes anyone without a headshot
// or an active listing. Honest empty state — never a filler face.
export default function HostsPage() {
  const [listers, setListers] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/listers/directory")
      .then((res) => alive && setListers(Array.isArray(res.data?.listers) ? res.data.listers : []))
      .catch(() => alive && setListers([]));
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen bg-night pb-24 md:pb-10">
      <NavTop />
      <main className="mx-auto max-w-5xl px-4 pt-8 sm:px-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-night-text">
          <Users size={20} className="text-brand-primary" /> Hosts and agents
        </h1>
        <p className="mt-1 text-sm text-night-sub">
          Meet the owners and realtors behind the homes. Tap a host to see everything they list.
        </p>

        {!listers ? (
          <div className="mt-16 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-night-line border-t-brand-primary" />
          </div>
        ) : listers.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-night-line bg-night-surface p-10 text-center">
            <Users className="mx-auto text-night-text" size={26} strokeWidth={1.6} />
            <h2 className="mt-3 text-lg font-semibold text-night-text">No hosts to show yet</h2>
            <p className="mt-1 text-sm text-night-sub">
              As owners and agents publish homes with a photo, they will appear here.
            </p>
          </div>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {listers.map((h) => (
              <Link
                key={h.id}
                to={`/hosts/${h.id}`}
                className="rounded-2xl border border-night-line bg-night-surface p-5 shadow-hairline transition hover:shadow-ambient"
              >
                <div className="flex items-center gap-3">
                  {h.photo_url ? (
                    <img src={h.photo_url} alt={h.name} className="h-14 w-14 rounded-full object-cover" loading="lazy" />
                  ) : (
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary text-lg font-semibold text-white">
                      {(h.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 font-semibold text-night-text">
                      <span className="truncate">{h.name || "Host"}</span>
                      {h.verified && <BadgeCheck size={15} className="shrink-0 text-brand-primary" />}
                    </p>
                    <p className="text-xs text-night-sub">
                      {h.is_realtor ? "Licensed realtor" : "Private host"}
                      {" · "}
                      {h.listing_count} home{h.listing_count === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>

                {h.listings?.length > 0 && (
                  <div className="mt-4 flex gap-2">
                    {h.listings.slice(0, 3).map((l) => (
                      <img
                        key={l.id}
                        src={l.photos?.[0]}
                        alt={l.title}
                        loading="lazy"
                        className="h-16 flex-1 rounded-lg object-cover"
                      />
                    ))}
                  </div>
                )}
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-primary">
                  <Home size={13} /> See all listings
                </span>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
